/** Consolas servidas por emuladores externos (Selkies no browser), não EmulatorJS. */

export const EXTERNAL_EMULATORS = {
  '3ds': {
    label: 'Nintendo 3DS',
    emulatorName: 'Azahar',
    playPath: '/play/3ds/',
    exts: new Set(['3ds', 'cci', 'cia']),
  },
  wii: {
    label: 'Nintendo Wii / GameCube',
    emulatorName: 'Dolphin',
    playPath: '/play/wii/',
    exts: new Set(['wbfs', 'iso', 'gcm', 'gc', 'wad', 'rvz', 'ciso']),
  },
};

const TOKEN_KEY = 'games_access_token';

const EXT_TO_EXTERNAL = new Map();
for (const [key, cfg] of Object.entries(EXTERNAL_EMULATORS)) {
  for (const ext of cfg.exts) EXT_TO_EXTERNAL.set(ext, key);
}

export function isExternalEmulatorKey(ejsKey) {
  return ejsKey in EXTERNAL_EMULATORS;
}

export function externalEmulatorForExt(filename) {
  const dot = String(filename || '').lastIndexOf('.');
  if (dot < 0) return null;
  return EXT_TO_EXTERNAL.get(filename.slice(dot + 1).toLowerCase()) || null;
}

export function externalEmulatorConfig(ejsKey) {
  return EXTERNAL_EMULATORS[ejsKey] || null;
}

/**
 * Origem do Selkies (Azahar/Dolphin).
 * Tem de ser o backend directo (games-api) — Cloudflare Pages não faz proxy WebSocket.
 */
function playBackendOriginFromLocation() {
  if (typeof window === 'undefined') return '';

  const { origin, hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${window.location.protocol}//${hostname}:5221`;
  }
  if (origin.includes('games.cassiuslabs.com')) {
    return origin.replace('games.cassiuslabs.com', 'games-api.cassiuslabs.com');
  }
  if (origin.includes('games.auditorfiscal.com')) {
    return origin.replace('games.auditorfiscal.com', 'games-api.auditorfiscal.com');
  }
  return origin;
}

function envPlayBaseUrl() {
  const env = import.meta.env.VITE_PLAY_BASE_URL;
  if (!env) return null;
  return String(env).replace(/\/$/, '');
}

function isLocalUrl(url) {
  return /localhost|127\.0\.0\.1/.test(url);
}

let cachedPlayBase = null;

/** Lê playBaseUrl do games-backend (fiável em produção). */
export async function resolvePlayBackendOrigin() {
  if (cachedPlayBase) return cachedPlayBase;

  const envUrl = envPlayBaseUrl();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const localBrowser = hostname === 'localhost' || hostname === '127.0.0.1';

  if (envUrl && (!isLocalUrl(envUrl) || localBrowser)) {
    cachedPlayBase = envUrl;
    return cachedPlayBase;
  }

  if (typeof window !== 'undefined') {
    try {
      const r = await fetch(`${window.location.origin}/api/config`, {
        credentials: 'include',
        cache: 'no-store',
      });
      if (r.ok) {
        const data = await r.json();
        const fromApi = data?.playBaseUrl ? String(data.playBaseUrl).replace(/\/$/, '') : '';
        if (fromApi) {
          cachedPlayBase = fromApi;
          return cachedPlayBase;
        }
      }
    } catch {
      /* fallback abaixo */
    }
  }

  cachedPlayBase = playBackendOriginFromLocation();
  return cachedPlayBase;
}

/** Síncrono — preferir resolvePlayBackendOrigin() antes de abrir iframe. */
export function playBackendOrigin() {
  if (cachedPlayBase) return cachedPlayBase;

  const envUrl = envPlayBaseUrl();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const localBrowser = hostname === 'localhost' || hostname === '127.0.0.1';

  if (envUrl && (!isLocalUrl(envUrl) || localBrowser)) return envUrl;
  return playBackendOriginFromLocation();
}

function withPlayAuth(url) {
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}access_token=${encodeURIComponent(token)}`;
  } catch {
    return url;
  }
}

/** URL pública do Selkies — sempre via backend (WebSocket). */
export function externalPlayUrl(platform, cacheBust, playOrigin) {
  const cfg = EXTERNAL_EMULATORS[platform];
  if (!cfg) return null;

  const origin = (playOrigin || playBackendOrigin()).replace(/\/$/, '');
  const base = `${origin}${cfg.playPath}`;
  let url = base;
  if (cacheBust) {
    const sep = url.includes('?') ? '&' : '?';
    url = `${url}${sep}_=${encodeURIComponent(String(cacheBust))}`;
  }
  return withPlayAuth(url);
}

/**
 * Origem da API para launch/upload.
 * Sempre games-api em produção — Cloudflare Pages corta bodies grandes (ROM 3DS).
 * O iframe Selkies pode continuar same-origin (playBaseUrl).
 */
function gamesApiOriginFromLocation() {
  if (typeof window === 'undefined') return '';
  const { origin, hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:5221`;
  }
  if (hostname === 'games.cassiuslabs.com' || hostname.endsWith('.pages.dev')) {
    return 'https://games-api.cassiuslabs.com';
  }
  if (hostname === 'games.auditorfiscal.com') {
    return 'https://games-api.auditorfiscal.com';
  }
  if (origin.includes('games.cassiuslabs.com')) {
    return origin.replace('games.cassiuslabs.com', 'games-api.cassiuslabs.com');
  }
  if (origin.includes('games.auditorfiscal.com')) {
    return origin.replace('games.auditorfiscal.com', 'games-api.auditorfiscal.com');
  }
  return origin;
}

async function externalLaunchApiUrl() {
  if (typeof window === 'undefined') return '/api/external-launch';
  return `${gamesApiOriginFromLocation().replace(/\/$/, '')}/api/external-launch`;
}

function authHeaders() {
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export async function requestExternalLaunch(_appUrlFn, platform, romFilename) {
  const r = await fetch(await externalLaunchApiUrl(), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ platform, rom: romFilename }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

/** Envia ROM do browser para o servidor e enfileira abertura no Azahar/Dolphin. */
export async function uploadAndLaunchExternal(platform, file) {
  const form = new FormData();
  form.append('platform', platform);
  form.append('rom', file, file.name);

  const url = await externalLaunchApiUrl();
  const r = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { ...authHeaders() },
    body: form,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const hint =
      r.status === 413 || r.status === 502 || r.status === 504
        ? ' (ficheiro grande — confirma que o upload vai para games-api, não Pages)'
        : '';
    throw new Error((data.error || `HTTP ${r.status}`) + hint);
  }
  return data;
}

/** Abre emulador externo: enfileira ROM (servidor ou upload) e mostra iframe Selkies. */
export async function launchExternalSession({
  platform,
  title,
  filename,
  file = null,
  onSession,
  onPlayed,
}) {
  const cfg = externalEmulatorConfig(platform);
  if (!cfg) throw new Error('Plataforma externa inválida');

  onSession({
    platform,
    title,
    filename: file?.name || filename,
    emulatorName: cfg.emulatorName,
    platformLabel: cfg.label,
    iframeUrl: null,
    launchStatus: `A preparar ${cfg.emulatorName}…`,
  });

  try {
    if (file) {
      const mb = Math.max(1, Math.round(file.size / 1024 / 1024));
      onSession({
        platform,
        title,
        filename: file.name,
        emulatorName: cfg.emulatorName,
        platformLabel: cfg.label,
        iframeUrl: null,
        launchStatus: `A enviar ROM local (${mb} MiB) para o servidor…`,
      });
      await uploadAndLaunchExternal(platform, file);
    } else {
      await requestExternalLaunch(null, platform, filename);
    }
    onPlayed?.();
    const playOrigin = await resolvePlayBackendOrigin();
    await new Promise((r) => window.setTimeout(r, 4000));
    onSession({
      platform,
      title,
      filename: file?.name || filename,
      emulatorName: cfg.emulatorName,
      platformLabel: cfg.label,
      iframeUrl: externalPlayUrl(platform, Date.now(), playOrigin),
      launchStatus: '',
    });
  } catch (err) {
    const playOrigin = await resolvePlayBackendOrigin().catch(() => playBackendOrigin());
    onSession({
      platform,
      title,
      filename: file?.name || filename,
      emulatorName: cfg.emulatorName,
      platformLabel: cfg.label,
      iframeUrl: externalPlayUrl(platform, undefined, playOrigin),
      launchStatus: `Não foi possível abrir automaticamente: ${err instanceof Error ? err.message : String(err)}`,
    });
  }
}
